/*
 * Purpose: Compound component combining CreatePassword and OTP for reset/update flows.
 * Boundary: Composition only; wires hook state into layout; delegates to child components.
 * Invariants:
 * - Exposes three states: isPasswordValid, isOtpValid, isValid (both).
 * - Exposes selectedChannel for the parent DTO.
 * - Delegates password logic to CreatePassword, OTP logic to Otp.
 * - ASCII only; no Unicode in comments or strings.
 */

// Imports
import { createContext, useContext, useEffect } from "react";
import PasswordWithOtpLayout from "./passwordwithotp/passwordwithotp-layout";
import { usePasswordWithOtp } from "./passwordwithotp/passwordwithotp-hooks";

// Context for sharing compound state upward
export const PasswordWithOtpContext = createContext();

// Component PasswordWithOtp:
// Headless wrapper syncing compound state with parent via context
export default function PasswordWithOtp({
    auth = {},
    type = "reset",
    idNumber = '',
    cellNumber = '',
    isValidID = false,
    isProceedReadyOtp = false,
    heading
}) {
    // State Management - initialize hook
    const props = usePasswordWithOtp({
        auth,
        type,
        idNumber,
        cellNumber,
        isValidID,
        isProceedReadyOtp,
        heading
    });

    // Context - get setter from parent
    const { setPasswordWithOtpState } = useContext(PasswordWithOtpContext) || {};

    // Effect: Update parent via context with compound state including selectedChannel
    useEffect(() => {
        if (setPasswordWithOtpState) {
            setPasswordWithOtpState({
                isPasswordValid: props.isPasswordValid,
                isOtpValid: props.isOtpValid,
                isValid: props.isValid,
                password: props.password,
                otpValue: props.otpValue,
                selectedChannel: props.selectedChannel
            });
        } else {
            console.error('[PasswordWithOtp Container] NO setPasswordWithOtpState in context!');
        }
    }, [props.isPasswordValid, props.isOtpValid, props.isValid, props.password, props.otpValue, props.selectedChannel, setPasswordWithOtpState]);

    // Rendering - delegate structure and UI to layout
    return <PasswordWithOtpLayout {...props} />;
}

// Metadata - friendly name for React DevTools
PasswordWithOtp.displayName = "PasswordWithOtp";
